// THE SHOP DRAWING THIS ISSUE IS PAIRED WITH - one piece of work, one set of
// hours.
//
// Owner, 2026-10-08: "once a selection made from either sides, the other side
// should reflect the link". The pair could only be made in the two creation
// forms and was shown nowhere afterwards, so on 2026-10-10 he asked why it
// had not been deployed. This is the issue side of the answer: the linked
// drawing, the pair's ONE hours figure and where it was entered, and Link /
// Unlink for an issue that already exists.
//
// SEVERAL DRAWINGS (owner, 2026-11-01: "one issue, many drawings"): every
// linked drawing is listed with its own Open / Unlink, "Link another" adds
// one, and the hours line says the lower side counts - the issue's hours or
// its drawings' total, never both.
//
// Everything about the pair is decided on the server (back/utils/
// issueDrawingPair): which drawings may be offered, who may link, and which
// side holds the hours. This only shows what it is told.
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Popconfirm, Select, Space, message } from "antd";
import { LinkOutlined, DisconnectOutlined } from "@ant-design/icons";
import { getIssuePair, pairableDrawings, pairIssueToDrawing, unpairIssue } from "api/issueDrawingPair";
import { T } from "pages/Issue/theme";
import { matchOption } from "components/SearchSelect/searchSelect";

const STATUS_WORDS = {
  requested: "requested", submitted: "submitted", under_review: "under review",
  approved: "approved", rejected: "returned", deleted: "deleted", missing: "missing",
};

export const hoursLine = (hours, hoursOn, here, drawingCount = 1) => {
  if (!(Number(hours) > 0)) return "No hours entered yet - enter them on either side, once.";
  const h = `${hours} hour${Number(hours) === 1 ? "" : "s"}`;
  if (drawingCount > 1) {
    return `${h} counted, from the ${hoursOn === "issue" ? "issue" : `${drawingCount} drawings`} - the lower side counts, never both.`;
  }
  return hoursOn === here
    ? `${h}, entered here - they count for both.`
    : `${h}, entered on the ${hoursOn === "issue" ? "issue" : "shop drawing"} - they count for both.`;
};

export default function IssueShopDrawingLink({ issue, onChanged }) {
  const history = useHistory();
  const issueId = issue && issue._id ? String(issue._id) : "";
  const [pair, setPair] = useState(null);
  const [picking, setPicking] = useState(false);
  const [options, setOptions] = useState(null);
  const [chosen, setChosen] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!issueId) return;
    getIssuePair(issueId).then(setPair).catch(() => setPair(null));
  }, [issueId]);
  useEffect(() => { load(); }, [load]);

  const startPicking = useCallback(async () => {
    setPicking(true);
    setOptions(null);
    try {
      const d = await pairableDrawings(pair.projectId, issueId);
      setOptions((d && d.drawings) || []);
    } catch (e) {
      message.error(e.message || "Could not list the shop drawings");
      setPicking(false);
    }
  }, [pair, issueId]);

  const link = useCallback(async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const r = await pairIssueToDrawing(issueId, chosen);
      message.success(r && r.delivery && r.delivery.triggered
        ? "Linked. The issue has a sheet attached, so the drawing is queued for delivery."
        : "Linked - the two now count one set of hours");
      setPicking(false); setChosen(undefined);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      message.error(e.message || "Could not link them");
    } finally { setBusy(false); }
  }, [chosen, issueId, load, onChanged]);

  const unlink = useCallback(async (drawingId) => {
    setBusy(true);
    try {
      await unpairIssue(issueId, drawingId);
      message.success("Unlinked. Hours already entered were left where they are.");
      load();
      if (onChanged) onChanged();
    } catch (e) {
      message.error(e.message || "Could not unlink them");
    } finally { setBusy(false); }
  }, [issueId, load, onChanged]);

  // Nothing linked and nothing this person may do about it: say nothing.
  if (!pair || (!pair.drawing && !pair.canLink)) return null;
  const list = pair.drawings && pair.drawings.length ? pair.drawings : (pair.drawing ? [pair.drawing] : []);
  const d = list[0];
  const many = list.length > 1;

  return (
    <div style={{ margin: "8px 0", padding: "8px 12px", background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: T.muted }}>SHOP DRAWING</span>
        {d ? (
          <>
            <span style={{ color: T.text, fontSize: 12 }}>{hoursLine(pair.hours, pair.hoursOn, "issue", list.length)}</span>
            {pair.canLink && !picking ? (
              <Button size="small" icon={<LinkOutlined />} onClick={startPicking} style={{ marginLeft: "auto" }}>
                Link another
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <span style={{ color: T.muted, fontSize: 12 }}>Not linked</span>
            {!picking ? (
              <Button size="small" icon={<LinkOutlined />} onClick={startPicking} style={{ marginLeft: "auto" }}>
                Link a shop drawing
              </Button>
            ) : null}
          </>
        )}
      </div>
      {list.map((x) => (
        <div key={x._id} data-testid="linked-drawing" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
          <span style={{ color: T.ink, fontWeight: 500 }}>{[x.sheetNumber, x.name].filter(Boolean).join(" - ")}</span>
          <span style={{ color: T.muted, fontSize: 12 }}>{STATUS_WORDS[x.status] || x.status}</span>
          {many && Number(x.hours) > 0 ? <span style={{ color: T.muted, fontSize: 12 }}>{x.hours} h on this drawing</span> : null}
          <Space size={4} style={{ marginLeft: "auto" }}>
            {x.status !== "missing" && x.status !== "deleted" ? (
              <Button size="small" onClick={() => history.push(`/shop-drawings/${x.projectId || pair.projectId}?drawing=${x._id}`)}>
                Open
              </Button>
            ) : null}
            {pair.canLink ? (
              <Popconfirm title="Unlink this shop drawing from the issue? Hours already entered stay where they are."
                          onConfirm={() => unlink(x._id)}>
                <Button size="small" icon={<DisconnectOutlined />} loading={busy}>Unlink</Button>
              </Popconfirm>
            ) : null}
          </Space>
        </div>
      ))}
      {picking ? (
        <div style={{ marginTop: 8 }}>
          {options === null ? (
            <span style={{ color: T.muted, fontSize: 12 }}>Loading the shop drawings...</span>
          ) : options.length === 0 ? (
            <Space>
              <span style={{ color: T.text, fontSize: 12 }}>
                No shop drawing on this project can be linked: only requests that are not yet delivered or approved, and not already linked to another issue.
              </span>
              <Button size="small" onClick={() => setPicking(false)}>Close</Button>
            </Space>
          ) : (
            <Space wrap>
              <Select filterOption={matchOption}
                showSearch
                optionFilterProp="label"
                placeholder="Pick a shop drawing request"
                style={{ minWidth: 320 }}
                value={chosen}
                onChange={setChosen}
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 760 }}
                options={options.map((o) => ({
                  value: o._id,
                  // The drawing's activity path rides in the label, so the
                  // search finds it by level, area or parent (owner, 2026-10-13).
                  label: [o.sheetNumber, o.name].filter(Boolean).join(" - ")
                    + (o.activityFullPath ? `  ·  ${o.activityFullPath}` : "")
                    + (o.hours ? `  (${o.hours} h already on the drawing)` : ""),
                  title: o.activityFullPath || o.name,
                }))}
              />
              <Button size="small" type="primary" disabled={!chosen} loading={busy} onClick={link}>Link</Button>
              <Button size="small" onClick={() => { setPicking(false); setChosen(undefined); }}>Cancel</Button>
              <span style={{ color: T.muted, fontSize: 12 }}>The two then count one set of hours.</span>
            </Space>
          )}
        </div>
      ) : null}
    </div>
  );
}
